import { Controller } from '@hotwired/stimulus'

// Live audience preview for the segment criteria form. The controller sits on the
// <form>; any input/change bubbles to its debounced #refresh, which serializes ALL
// criteria fields and fetches the count from #preview, writing the total and
// undeliverable count into the live-count targets.
//
// Blank / all-blank criteria resolve to 0 on the server (ContactSegment#members'
// Contact.none guard), which we present as the disabled/empty state — so the form
// agrees with the model that "no filters" means nobody, not everyone.
//
// Out-of-order responses are dropped with a minimal monotonic request sequence
// (increment per fetch; ignore any response whose seq isn't the latest) rather than
// an AbortController — there is no AbortController precedent in this codebase.
export default class extends Controller {
  static targets = ['count', 'total', 'undeliverable', 'empty']
  static values = { url: String }

  connect() {
    this.seq = 0
    // Prime the count on load so an edited segment shows its current audience.
    this.refresh()
  }

  disconnect() {
    clearTimeout(this.debounce)
  }

  refresh() {
    clearTimeout(this.debounce)
    this.debounce = setTimeout(() => this.fetchCount(), 200)
  }

  async fetchCount() {
    const seq = ++this.seq
    const query = new URLSearchParams(new FormData(this.element)).toString()
    let data
    try {
      const response = await fetch(`${this.urlValue}?${query}`, {
        headers: { Accept: 'application/json' }
      })
      if (!response.ok) return
      data = await response.json()
    } catch {
      return
    }
    if (seq !== this.seq) return // a newer request already returned — ignore this stale one
    this.render(data)
  }

  render(data) {
    const total = Number(data.total) || 0
    if (total > 0) {
      this.totalTarget.textContent = total
      if (this.hasUndeliverableTarget)
        this.undeliverableTarget.textContent = Number(data.undeliverable_count) || 0
      this.countTarget.hidden = false
      this.emptyTarget.hidden = true
    } else {
      this.countTarget.hidden = true
      this.emptyTarget.hidden = false
    }
  }
}
